import {
  ThemeProvider,
  Button,
  ToggleButtonGroup,
  Tooltip,
  ToggleButton,
} from "@mui/material";
import MUIDataTable from "mui-datatables";
import React, { useContext, useEffect, useState } from "react";
import { Vocabulary } from "../Utils/Vocabulary";
import genericStyles from "../Styles/genericStyles.module.css";
import { Edit, Delete, ViewColumn } from "@mui/icons-material";
import GenericModal from "./GenericModal";
import AddCircleIcon from "@mui/icons-material/AddCircle";
import { deleteData } from "../Services/deleteData";
import { getData } from "../Services/getData";
import { useNavigate } from "react-router-dom";
import { ETableEditAction } from "../Utils/Enums";
import { MUIDataTableCustomStyleProps } from "../Utils/Types";
import StandardActionButtons from "./StandarsActionButtons";
import { isMobile } from "../Utils/Utils";
import useQueryStringParser from "../Hooks/useQueryStringParser";
import Config from "../Utils/Config";
import useCustomSearchParam from "../Hooks/useCustomSearchParam";
import { tableTheme } from "../Utils/Constants";
import { WebSocketContext } from "../Context/WebSocketContext";

export default function MUIDataTableCustomStyle(
  props: MUIDataTableCustomStyleProps
) {
  const [openDeleteModal, setOpenDeleteModal] = useState(false);
  const [tableData, setTableData]: any = useState([]);
  const [count, setCount] = useState(0);
  const [perPage, setPerPage] = useState(Config.rowsPerPage);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const navigate = useNavigate();
  const customSearchParams = useCustomSearchParam();
  const [searchText, setSearchText] = useState("");
  const websocketContext = useContext(WebSocketContext);
  useQueryStringParser((data: any) => {
    if (data) getDataTable(data);
  });

  /**
   * Adds a URL parameter to the provided Map if it doesn't already exist.
   * @param key - The key for the URL parameter.
   * @param value - The value of the URL parameter.
   * @param addedParams - The Map to store the parameters.
   */
  const addUrlParam = (
    key: string,
    value: string | number | boolean | null | undefined,
    addedParams: Map<string, string>
  ) => {
    if (value !== undefined && value !== null && !addedParams.has(key)) {
      addedParams.set(key, encodeURIComponent(value.toString())); // Add new parameter to the Map if it doesn't exist
    }
  };

  /**
   *
   * @param options
   * @returns
   */
  const generateURL = (options?: Record<string, any>) => {
    // Parse the query parameters from window.location.search
    const urlParams = new URLSearchParams(window.location.search);
    const addedParams = new Map<string, string>(); // Tracks added parameters
    // Determine the page and perPage values
    const page = options?.page
      ? parseInt(options.page)
      : parseInt(urlParams.get("page") || Config.standardPage);

    const perPage = options?.perPage
      ? parseInt(options.perPage)
      : parseInt(urlParams.get("perPage") || Config.rowsPerPage);

    // Use the Map to track added params and avoid duplicates
    addUrlParam("page", page, addedParams);
    addUrlParam("perPage", perPage, addedParams);

    if (options && Object.keys(options).length > 0) {
      // Add the query parameters from options to the URL (these will override any parameters from urlParams)
      for (const key in options) {
        if (Object.prototype.hasOwnProperty.call(options, key)) {
          const value = options[key];
          addUrlParam(key, value, addedParams);
        }
      }
    } else {
      // Add the query parameters from urlParams to the URL (this includes all existing query params)
      urlParams.forEach((value, key) => {
        addUrlParam(key, value, addedParams);
      });
    }

    // Add the filters from props.filters, ensuring no duplicates from options or urlParams
    if (props.filters) {
      for (const key in props.filters) {
        const value = props.filters[key];
        addUrlParam(key, value, addedParams);
      }
    }

    // Generate the final URL from the Map
    const urlQuery = Array.from(addedParams.entries())
      .map(([key, value]) => `${key}=${value}`)
      .join("&");
    const url = `${props.CRUDEndPoint}/paginated?${urlQuery}`;
    return url;
  };

  /**
   *
   * @param options
   * @returns
   */
  const getDataTable = async (options?: Record<string, any>) => {
    const url = generateURL(options);
    const res = (await getData(url)) as any;
    if (!res || res?.error) {
      return;
    }
    setSearchText(options?.search ? options?.search : "");
    setTableData(res.data.results);
    setCount(res.data.numberOfResults);
  };

  /**
   *
   * @param data
   * @returns
   */
  const customGetDataTable = async (data: any) => {
    if (data) {
      const url = data;
      const res = (await getData(url)) as any;
      if (!res || res?.error) {
        return;
      }
      setTableData(res.data.results);
      setCount(res.data.numberOfResults);
    } else {
      getDataTable();
    }
  };

  /**
   *
   */
  useEffect(() => {
    if (props.customTriggerReload !== undefined) {
      //TO DO: should consider existing filters or delete them from url
      customGetDataTable(props.reloadURL);
    }
  }, [props.customTriggerReload]);

  /**
   *
   */
  useEffect(() => {
    if (props.triggerReload !== undefined) {
      //if query contains params remove them, else get data
      getDataTable();
    }
  }, [props.triggerReload]);

  /**
   *
   * @param id
   */
  const deleteElement = async () => {
    const url = `${props.CRUDEndPoint}/${selectedId}`;
    const res = (await deleteData(url)) as any;
    if (!res || res?.error) {
      return;
    }
    if (props.shouldTriggerStorageUpdate) {
      websocketContext.setShouldUpdate(!websocketContext.shouldUpdate);
    }
    setSelectedId(null);
    setOpenDeleteModal(false);
    if (props.setContextValue) props.setContextValue([]);
    if (props?.handleInlineForm) props?.handleInlineForm();
    getDataTable();
  };

  /**
   * Actions columns constant
   */
  const actionsColumns = [
    ...props.columns,
    {
      label: "",
      name: "",
      options: {
        setCellHeaderProps: () => ({
          align: "center",
        }),
        setCellProps: () => ({
          align: "center",
        }),
        filter: false,
        sort: false,
        display: props.hideOptions ? false : true,
        empty: true,
        customBodyRenderLite: (rowIndex: number, value: any) => {
          return (
            <>
              <div style={{ float: "right" }}>
                <ToggleButtonGroup exclusive aria-label="text alignment">
                  {props.renderCustomColumns
                    ? props.renderCustomColumns(rowIndex, tableData[rowIndex])
                    : null}
                  {props?.hideEditButton ? null : (
                    <Tooltip title={Vocabulary.edit}>
                      <ToggleButton
                        onClick={(e: any) => {
                          handleEditEntity(rowIndex, e);
                        }}
                        value="center"
                        aria-label="centered"
                      >
                        <Edit />
                      </ToggleButton>
                    </Tooltip>
                  )}
                  {props.hideDeleteButton ? null : (
                    <Tooltip title={Vocabulary.delete}>
                      <ToggleButton
                        disabled={tableData[rowIndex]?.shouldDelete === false}
                        onClick={(e: any) => {
                          e.stopPropagation();
                          setOpenDeleteModal(true);
                          setSelectedId(tableData[rowIndex]._id);
                        }}
                        value="center"
                        aria-label="centered"
                      >
                        <Delete />
                      </ToggleButton>
                    </Tooltip>
                  )}
                </ToggleButtonGroup>
              </div>
            </>
          );
        },
      },
    },
  ];

  /**
   *
   */
  const handleAddEntity = () => {
    const type = props.onEditAction;
    switch (type) {
      case ETableEditAction.MODAL:
        props?.handleModalForm(true);
        break;
      case ETableEditAction.REDIRECT:
        navigate(props.rowClickURL || "");
        break;
      case ETableEditAction.INLINE:
        return props?.handleInlineForm ? props?.handleInlineForm() : null;
      default:
        break;
    }
  };

  /**
   *
   * @param rowIndex
   * @param e
   */
  const handleEditEntity = (rowIndex: number, e: any) => {
    const type = props.onEditAction;
    switch (type) {
      case ETableEditAction.MODAL:
        e.stopPropagation();
        if (props.setContextValue) props.setContextValue(tableData[rowIndex]);
        if (props?.handleModalForm) props?.handleModalForm(true);
        if (props.addEntityIdToURL) {
          customSearchParams.searchParams.set("id", tableData[rowIndex]._id);
          customSearchParams.setSearchParams(customSearchParams.searchParams);
        }
        break;
      case ETableEditAction.REDIRECT:
        navigate(`${props.rowClickURL}${tableData[rowIndex]._id}`);
        break;
      case ETableEditAction.INLINE:
        return props?.handleInlineForm
          ? props?.handleInlineForm(tableData[rowIndex])
          : null;
      default:
        break;
    }
  };

  /**
   *
   * @returns
   */
  const getTableOptions = () => {
    const responsive: "standard" | "vertical" | "simple" | undefined =
      "standard";
    return {
      customToolbarSelect: props.customToolbarSelect,
      selectableRows: props.selectableRows ? props.selectableRows : "none",
      responsive: responsive,
      viewColumns: props.viewColumns || false,
      rowsPerPage: perPage,
      rowsPerPageOptions: Config.rowsPerPageOptions,
      confirmFilters: true,
      filter: false,
      print: false,
      download: false,
      fixedHeader: true,
      serverSide: true,
      searchOpen: false,
      searchText: searchText,
      textLabels: {
        body: {
          noMatch: Vocabulary.noResultsFound,
        },
        pagination: {
          next: Vocabulary.nextPage,
          previous: Vocabulary.previousPage,
          rowsPerPage: Vocabulary.rowsPerPage,
          displayRows: Vocabulary.of,
        },
        toolbar: {
          search: Vocabulary.search,
          downloadCsv: Vocabulary.downloadCSV,
          print: Vocabulary.print,
        },
        selectedRows: {
          text: Vocabulary.rowsSelected,
          delete: Vocabulary.delete,
        },
      },
      searchProps: {
        onKeyDown: (e: any) => {
          if (e.key === "Enter") {
            customSearchParams.addParamToQueryString("search", searchText);
            customSearchParams.addParamToQueryString("page", "0");
            window.scrollTo(0, 0);
          }
        },
      },
      onChangePage: (pageNumber: number) => {
        customSearchParams.addParamToQueryString("page", pageNumber);
        window.scrollTo(0, 0);
      },
      onChangeRowsPerPage: (numberOfRows: number) => {
        setPerPage(numberOfRows);
        customSearchParams.addParamToQueryString("perPage", numberOfRows);
        window.scrollTo(0, 0);
      },
      setRowProps: (row: any, dataIndex: any, rowIndex: any) => {
        return {
          style: {
            cursor: props.onRowClick ? "pointer" : "auto",
          },
          className: props.rowProps ? props.rowProps(row) : null,
        };
      },
      onSearchChange: async (search: any) => {
        setSearchText(search);
        if (!search || search?.length === 0) {
          customSearchParams.searchParams.delete("search");
          customSearchParams.setSearchParams(customSearchParams.searchParams);
        }
      },
      count: count,
      onRowsDelete: (rowData: any) => {
        //ask for confirmation
        const data = rowData?.data?.map(
          (row: any) => tableData[row.dataIndex]._id
        );
        setSelectedId(data.join(","));
        setOpenDeleteModal(true);
      },
      onRowClick: props.onRowClick
        ? (rowData: any, rowState: any) => {
          if (props.onRowClick)
            props?.onRowClick(tableData[rowState.rowIndex]);
        }
        : () => {
          return;
        },
      customToolbar: () => {
        return (
          <div>
            {props?.hideAddButton ? null : (
              <Button
                variant="contained"
                className={genericStyles.addButton}
                onClick={handleAddEntity}
              >
                <AddCircleIcon />
                {!isMobile() ? (
                  <div style={{ marginLeft: 5 }}>{Vocabulary.add}</div>
                ) : null}
              </Button>
            )}
            {React.isValidElement(props.buttons)
              ? props.buttons
              : props.buttons &&
              props.buttons?.map(
                (button: {
                  label: string;
                  handleClick: void;
                  icon: JSX.Element;
                }) => (
                  <Button
                    variant="contained"
                    className={genericStyles.addButton}
                  >
                    {button.icon && button.icon}
                    <div style={{ marginLeft: 5 }}>
                      {button.label && button.label}
                    </div>
                  </Button>
                )
              )}
          </div>
        );
      },
      ...(props.customRowRender && {
        customRowRender: (rowData: any, dataIndex: any, rowIndex: any) => {
          return props.customRowRender
            ? props.customRowRender(rowData, dataIndex, rowIndex, tableData)
            : null;
        },
      }),
    };
  };

  const CustomViewColumnIcon = () => {
    return (
      <>
        <ViewColumn />
        <span style={{ marginLeft: 5, fontSize: 10 }}>
          {Vocabulary.view}
          <br />
          {Vocabulary.columns}
        </span>
      </>
    );
  };

  const components: any = {
    icons: {
      ViewColumnIcon: CustomViewColumnIcon,
    },
  };

  return (
    <div>
      <ThemeProvider theme={tableTheme}>
        <MUIDataTable
          title={props.title}
          data={tableData}
          columns={actionsColumns}
          options={getTableOptions()}
          components={components}
        />
      </ThemeProvider>
      <GenericModal
        open={openDeleteModal}
        onClose={() => {
          setOpenDeleteModal(false);
        }}
        style={{ zIndex: 9999 }}
        title={Vocabulary.confirmDeleteTitle}
        actions={
          <StandardActionButtons
            okAction={() => deleteElement()}
            cancelAction={() => setOpenDeleteModal(false)}
            okText={Vocabulary.yes}
            cancelText={Vocabulary.no}
          />
        }
      >
        <div>{Vocabulary.confirmDeleteMessage}</div>
      </GenericModal>
      {props.addOrEditForm}
    </div>
  );
}
